import { AUTH_CONFIG } from './auth0-variables';

var TENANT_QUERY_KEYS = ["tenant", "company", "domain", "sso", "client"];
var EMAIL_QUERY_KEYS = ["login_hint", "email"];

function normalizeValue(value) {
  return String(value || "").trim().toLowerCase();
}

function readFirstParam(params, keys) {
  for (var i = 0; i < keys.length; i++) {
    var value = params.get(keys[i]);
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

function extractEmailDomain(value) {
  var normalized = String(value || "").trim().toLowerCase();
  var atIndex = normalized.lastIndexOf("@");
  if (atIndex < 0 || atIndex === normalized.length - 1) {
    return "";
  }
  return normalized.slice(atIndex + 1);
}

function getTenantEntries() {
  return Object.entries(AUTH_CONFIG.ssoConnections || {});
}

function hasEnterpriseRedirectConfig(tenantConfig) {
  return Boolean(
    (tenantConfig && tenantConfig.connection) ||
    (tenantConfig && tenantConfig.organization)
  );
}

function findTenantEntry(candidate) {
  var normalizedCandidate = normalizeValue(candidate);
  if (!normalizedCandidate) {
    return null;
  }
  var entries = getTenantEntries();
  for (var i = 0; i < entries.length; i++) {
    var tenantConfig = entries[i][1];
    var aliases = tenantConfig.aliases || [];
    var domains = tenantConfig.domains || [];
    var all = aliases.concat(domains);
    for (var j = 0; j < all.length; j++) {
      var normalizedEntry = normalizeValue(all[j]);
      if (
        normalizedEntry === normalizedCandidate ||
        normalizedCandidate.endsWith("." + normalizedEntry) ||
        normalizedCandidate.indexOf(normalizedEntry) >= 0
      ) {
        return entries[i];
      }
    }
  }
  return null;
}

// Whether a company lets everyone in its domains use SSO. Default true — only
// an explicit allowAllUsers === false restricts SSO to the users list.
function tenantAllowsAllUsers(tenantConfig) {
  return tenantConfig && tenantConfig.allowAllUsers !== false;
}

// Domain/alias match, but only honored when the matched company allows all of
// its domain's users. When restricted, only entries in users[] route to SSO.
function findTenantByDomain(candidate) {
  var entry = findTenantEntry(candidate);
  if (entry && !tenantAllowsAllUsers(entry[1])) {
    return null;
  }
  return entry;
}

// Exact-email match against a tenant's individual users allow-list.
function findTenantByEmail(email) {
  var normalized = normalizeValue(email);
  if (!normalized || normalized.indexOf('@') < 0) {
    return null;
  }
  var entries = getTenantEntries();
  for (var i = 0; i < entries.length; i++) {
    var users = entries[i][1].users || [];
    for (var j = 0; j < users.length; j++) {
      if (normalizeValue(users[j]) === normalized) {
        return entries[i];
      }
    }
  }
  return null;
}

function findTenantFromHostname(hostname) {
  var normalizedHost = normalizeValue(hostname);
  if (!normalizedHost) {
    return null;
  }
  var entries = getTenantEntries();
  for (var i = 0; i < entries.length; i++) {
    var tenantConfig = entries[i][1];
    var domains = tenantConfig.domains || [];
    for (var j = 0; j < domains.length; j++) {
      var normalizedDomain = normalizeValue(domains[j]);
      if (
        normalizedHost === normalizedDomain ||
        normalizedHost.endsWith("." + normalizedDomain)
      ) {
        return entries[i];
      }
    }
  }
  return null;
}

function getTenantFromPath(pathname) {
  var segments = String(pathname || "")
    .split("/")
    .filter(Boolean)
    .map(normalizeValue);

  if (segments.length >= 2 && segments[0] === "sso") {
    return segments[1];
  }
  return "";
}

export function resolveSsoRoute(locationLike) {
  var loc = locationLike || window.location;
  var params = new URLSearchParams((loc && loc.search) || "");
  var explicitConnection = readFirstParam(params, ["connection"]);
  var explicitOrganization = readFirstParam(params, ["organization"]);
  var loginHint = readFirstParam(params, EMAIL_QUERY_KEYS);
  var requestedTenant = readFirstParam(params, TENANT_QUERY_KEYS);
  var emailDomain = extractEmailDomain(loginHint);
  var pathTenant = getTenantFromPath(loc && loc.pathname);

  var tenantEntry =
    findTenantByEmail(loginHint) ||
    findTenantEntry(requestedTenant) ||
    findTenantEntry(pathTenant) ||
    findTenantByDomain(emailDomain) ||
    findTenantFromHostname(loc && loc.hostname);

  return {
    explicitConnection: explicitConnection,
    explicitOrganization: explicitOrganization,
    loginHint: loginHint,
    tenantKey: tenantEntry ? tenantEntry[0] : null,
    tenantConfig: tenantEntry ? tenantEntry[1] : null,
  };
}

export function getSsoLoginOptions(locationLike) {
  var resolved = resolveSsoRoute(locationLike);
  var explicitConnection = resolved.explicitConnection;
  var explicitOrganization = resolved.explicitOrganization;
  var loginHint = resolved.loginHint;
  var tenantKey = resolved.tenantKey;
  var tenantConfig = resolved.tenantConfig;

  var canUseTenantRedirect = hasEnterpriseRedirectConfig(tenantConfig);
  var shouldApplyTenantHints = Boolean(
    explicitConnection || explicitOrganization || canUseTenantRedirect
  );

  var options = {};

  if (explicitConnection) {
    options.connection = explicitConnection;
  } else if (canUseTenantRedirect && tenantConfig && tenantConfig.connection) {
    options.connection = tenantConfig.connection;
  }

  if (explicitOrganization) {
    options.organization = explicitOrganization;
  } else if (canUseTenantRedirect && tenantConfig && tenantConfig.organization) {
    options.organization = tenantConfig.organization;
  }

  if (loginHint && (!tenantConfig || shouldApplyTenantHints)) {
    options.login_hint = loginHint;
  }

  var primaryDomain = tenantConfig && tenantConfig.domains && tenantConfig.domains[0];
  if (primaryDomain && shouldApplyTenantHints) {
    options.domain_hint = primaryDomain;
  }

  return {
    tenantKey: tenantKey,
    options: options,
  };
}

export function getLoginRedirectOptionsForIdentifier(identifier) {
  var loginHint = String(identifier || "").trim();
  var emailDomain = extractEmailDomain(loginHint);
  // Exact email allow-list wins over domain matching (most specific first).
  // Domain matching only applies when the company allows all of its domain's users.
  var tenantEntry = findTenantByEmail(loginHint) || (emailDomain ? findTenantByDomain(emailDomain) : null);
  var tenantKey = tenantEntry ? tenantEntry[0] : null;
  var tenantConfig = tenantEntry ? tenantEntry[1] : null;
  var isEmail = Boolean(emailDomain);

  var options = {};

  if (loginHint) {
    options.login_hint = loginHint;
  }

  if (!isEmail && AUTH_CONFIG.basicConnection) {
    options.connection = AUTH_CONFIG.basicConnection;
  }

  if (tenantConfig && tenantConfig.connection) {
    options.connection = tenantConfig.connection;
  }

  if (tenantConfig && tenantConfig.organization) {
    options.organization = tenantConfig.organization;
  }

  var primaryDomain = tenantConfig && tenantConfig.domains && tenantConfig.domains[0];
  if ((tenantConfig && (tenantConfig.connection || tenantConfig.organization)) && primaryDomain) {
    options.domain_hint = primaryDomain;
  }

  return {
    loginHint: loginHint,
    isEmail: isEmail,
    tenantKey: tenantKey,
    tenantConfig: tenantConfig,
    mode: tenantConfig ? "sso" : "basic",
    options: options,
  };
}
